import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import Select from 'react-select';
import {
  getDashboardToken,
  fetchContracts,
  fetchContractMolecules,
  fetchSupplierMolecules,
  createContract,
  uploadContractDocument,
  updateContract,
} from '../services/dashboardService';
import { listSupplierDirectory } from '../services/supplierService';
import './Suppliers.css';

const API_URL = import.meta.env.VITE_API_URL || '';

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toInputDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
// Details Modal — shows molecules for a contract
// ─────────────────────────────────────────────────────────────────────────────
function DetailsModal({ contract, token, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!contract) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchContractMolecules(contract.id, token);
        if (!cancelled) setRows(data.rows || []);
      } catch {
        if (!cancelled) setErr('Failed to load molecules.');
      }
    })();
    return () => { cancelled = true; };
  }, [contract, token]);

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">Contract Molecules</span>
            <span className="sup-title-sub">{contract.supplier_name} &mdash; {fmtDate(contract.start_date)} to {fmtDate(contract.end_date)}</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sup-modal-body">
          {err && <div style={{ color: 'var(--red, #d63b0a)', marginBottom: 8 }}>{err}</div>}
          {!rows && !err && <div style={{ color: 'var(--t3)' }}>Loading...</div>}
          {rows && rows.length === 0 && <div style={{ color: 'var(--t3)' }}>No molecules linked.</div>}
          {rows && rows.length > 0 && (
            <table className="sup-table" style={{ minWidth: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: 300 }}>INN (Molecule)</th>
                  <th style={{ width: 140 }}>Product ID</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.inn}</td>
                    <td>{r.product_id ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// react-select styles (match existing sup-input / sup-select look)
// ─────────────────────────────────────────────────────────────────────────────
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    fontSize: 13,
    borderRadius: 8,
    borderColor: state.isFocused ? 'var(--teal, #0d9488)' : 'var(--brd, #dee2e6)',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(13,148,136,.15)' : 'none',
    '&:hover': { borderColor: 'var(--teal, #0d9488)' },
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: '6px 12px',
    backgroundColor: state.isSelected ? 'var(--teal, #0d9488)' : state.isFocused ? 'var(--teal3, #f0fdfa)' : '#fff',
    color: state.isSelected ? '#fff' : 'var(--navy, #0d2b3e)',
    cursor: 'pointer',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--teal3, #f0fdfa)',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: 12,
    color: 'var(--navy, #0d2b3e)',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--teal, #0d9488)',
    '&:hover': { backgroundColor: 'var(--teal, #0d9488)', color: '#fff' },
  }),
  placeholder: (base) => ({ ...base, fontSize: 13, color: 'var(--t3, #94a3b8)' }),
  input: (base) => ({ ...base, fontSize: 13 }),
};

const rsDisabledStyles = {
  ...rsStyles,
  control: (base, state) => ({
    ...rsStyles.control(base, state),
    backgroundColor: '#f1f5f9',
    pointerEvents: 'none',
  }),
};

// Checkbox option for molecules multi-select
function CheckboxOption(props) {
  return (
    <div
      ref={props.innerRef}
      {...props.innerProps}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', fontSize: 13, cursor: 'pointer',
        backgroundColor: props.isFocused ? 'var(--teal3, #f0fdfa)' : '#fff',
      }}
    >
      <input type="checkbox" checked={props.isSelected} readOnly style={{ accentColor: 'var(--teal, #0d9488)' }} />
      {props.label}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Contract Form Modal — shared for Add and Edit
// ─────────────────────────────────────────────────────────────────────────────
function ContractFormModal({ mode, contract, suppliers, token, onClose, onDone }) {
  const isEdit = mode === 'edit';

  const [form, setForm] = useState({
    supplier_id: isEdit ? (contract.supplier_id ?? '') : '',
    contact_name: isEdit ? (contract.contact_name ?? '') : '',
    contact_email: isEdit ? (contract.contact_email ?? '') : '',
    contact_phone: isEdit ? (contract.contact_phone ?? '') : '',
    signed_date: isEdit ? toInputDate(contract.signed_date) : '',
    start_date: isEdit ? toInputDate(contract.start_date) : '',
    end_date: isEdit ? toInputDate(contract.end_date) : '',
  });
  const [molecules, setMolecules] = useState([]);
  const [selectedMols, setSelectedMols] = useState([]);
  const [molLoading, setMolLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [docAction, setDocAction] = useState(null); // null=keep, 'remove', 'replace'
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  // Track whether initial molecule pre-selection has been done (edit mode)
  const initialMolsDone = useRef(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Supplier options
  const supplierOptions = useMemo(
    () => suppliers.map(s => ({ value: s.supplier_id, label: s.supplier_name })),
    [suppliers]
  );
  const selectedSupplier = useMemo(
    () => supplierOptions.find(o => String(o.value) === String(form.supplier_id)) || null,
    [supplierOptions, form.supplier_id]
  );

  // Load molecules when supplier changes
  useEffect(() => {
    if (!form.supplier_id) { setMolecules([]); setSelectedMols([]); return; }
    let cancelled = false;
    (async () => {
      setMolLoading(true);
      setMolecules([]);
      if (!isEdit || initialMolsDone.current) setSelectedMols([]);
      try {
        const data = await fetchSupplierMolecules(form.supplier_id, token);
        if (!cancelled) {
          const seen = new Set();
          const unique = [];
          for (const r of (data.rows || [])) {
            if (!seen.has(r.inn)) { seen.add(r.inn); unique.push(r); }
          }
          setMolecules(unique);
        }
      } catch {
        if (!cancelled) setMolecules([]);
      } finally {
        if (!cancelled) setMolLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [form.supplier_id, token, isEdit]);

  // Edit mode: load existing contract molecules and pre-select them once available molecules are loaded
  useEffect(() => {
    if (!isEdit || initialMolsDone.current || molecules.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchContractMolecules(contract.id, token);
        if (!cancelled) {
          const existing = (data.rows || []).map(r => r.inn);
          const availableInns = new Set(molecules.map(m => m.inn));
          // Pre-select molecules that are still in the available list
          const preselected = existing
            .filter(inn => availableInns.has(inn))
            .map(inn => ({ value: inn, label: inn }));
          setSelectedMols(preselected);
          initialMolsDone.current = true;
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, contract, molecules, token]);

  const molOptions = useMemo(
    () => molecules.map(m => ({ value: m.inn, label: m.inn })),
    [molecules]
  );

  const validate = () => {
    const e = {};
    if (!form.supplier_id) e.supplier_id = 'Required';
    if (!form.contact_name.trim()) e.contact_name = 'Required';
    if (!form.contact_email.trim()) e.contact_email = 'Required';
    if (!form.contact_phone.trim()) e.contact_phone = 'Required';
    if (!form.signed_date) e.signed_date = 'Required';
    if (!form.start_date) e.start_date = 'Required';
    if (!form.end_date) e.end_date = 'Required';
    if (form.start_date && form.end_date && form.end_date < form.start_date) e.end_date = 'End must be after start';
    if (selectedMols.length === 0) e.molecules = 'Select at least one molecule';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    setSaveErr('');
    try {
      const mols = selectedMols.map(opt => ({ inn: opt.value }));
      if (isEdit) {
        await updateContract(
          contract.id,
          form,
          mols,
          docAction,
          docAction === 'replace' ? file : null,
          token
        );
      } else {
        const body = {
          ...form,
          signed_date: form.signed_date || null,
          molecules: mols,
        };
        const result = await createContract(body, token);
        if (file && result.id) {
          await uploadContractDocument(result.id, file, token);
        }
      }
      onDone();
    } catch (err) {
      setSaveErr(err?.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} contract`);
    } finally {
      setSaving(false);
    }
  };

  const currentDocName = isEdit && contract.document_blob_name
    ? contract.document_blob_name.split('/').pop()
    : null;

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">{isEdit ? 'Edit Contract' : 'Add New Contract'}</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sup-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {saveErr && <div style={{ color: 'var(--red, #d63b0a)', fontSize: 13 }}>{saveErr}</div>}

          {/* Supplier — disabled on edit */}
          <div>
            <label className="sup-field-label">Supplier <span style={{ color: '#d63b0a' }}>*</span></label>
            <Select
              options={supplierOptions}
              value={selectedSupplier}
              onChange={opt => set('supplier_id', opt ? opt.value : '')}
              isClearable={!isEdit}
              isSearchable={!isEdit}
              isDisabled={isEdit}
              placeholder="Type to search suppliers..."
              styles={isEdit ? rsDisabledStyles : rsStyles}
              menuPortalTarget={document.body}
              noOptionsMessage={() => 'No matching suppliers'}
            />
            {errors.supplier_id && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.supplier_id}</div>}
          </div>

          {/* Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="sup-field-label">Contact Name <span style={{ color: '#d63b0a' }}>*</span></label>
              <input className="sup-input" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
              {errors.contact_name && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.contact_name}</div>}
            </div>
            <div>
              <label className="sup-field-label">Contact Email <span style={{ color: '#d63b0a' }}>*</span></label>
              <input className="sup-input" type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} />
              {errors.contact_email && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.contact_email}</div>}
            </div>
            <div>
              <label className="sup-field-label">Contact Phone <span style={{ color: '#d63b0a' }}>*</span></label>
              <input className="sup-input" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} />
              {errors.contact_phone && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.contact_phone}</div>}
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="sup-field-label">Signed Date <span style={{ color: '#d63b0a' }}>*</span></label>
              <input className="sup-input" type="date" value={form.signed_date} onChange={e => set('signed_date', e.target.value)} />
              {errors.signed_date && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.signed_date}</div>}
            </div>
            <div>
              <label className="sup-field-label">Start Date <span style={{ color: '#d63b0a' }}>*</span></label>
              <input className="sup-input" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
              {errors.start_date && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.start_date}</div>}
            </div>
            <div>
              <label className="sup-field-label">End Date <span style={{ color: '#d63b0a' }}>*</span></label>
              <input className="sup-input" type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
              {errors.end_date && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.end_date}</div>}
            </div>
          </div>

          {/* Molecules — searchable multi-select with checkboxes */}
          <div>
            <label className="sup-field-label">Molecules <span style={{ color: '#d63b0a' }}>*</span></label>
            {!form.supplier_id && <div style={{ fontSize: 12, color: 'var(--t3)' }}>Select a supplier first.</div>}
            {form.supplier_id && molLoading && <div style={{ fontSize: 12, color: 'var(--t3)' }}>Loading molecules...</div>}
            {form.supplier_id && !molLoading && (
              <Select
                isMulti
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={molOptions}
                value={selectedMols}
                onChange={vals => setSelectedMols(vals || [])}
                components={{ Option: CheckboxOption }}
                placeholder="Type to search molecules..."
                styles={rsStyles}
                menuPortalTarget={document.body}
                noOptionsMessage={() => 'No active molecules found for this supplier'}
              />
            )}
            {errors.molecules && <div style={{ color: '#d63b0a', fontSize: 12 }}>{errors.molecules}</div>}
          </div>

          {/* Document */}
          <div>
            <label className="sup-field-label">Document (optional)</label>
            {isEdit && currentDocName && docAction !== 'remove' && docAction !== 'replace' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '6px 0' }}>
                <i className="bi bi-file-earmark-check" style={{ color: 'var(--teal)', fontSize: 16 }}></i>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentDocName}</span>
                <button type="button" className="sup-btn" style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={() => { setDocAction('replace'); setFile(null); }}>Replace</button>
                <button type="button" className="sup-btn" style={{ padding: '2px 8px', fontSize: 11, color: '#d63b0a' }}
                  onClick={() => setDocAction('remove')}>Remove</button>
              </div>
            ) : isEdit && docAction === 'remove' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '6px 0', color: 'var(--t3)' }}>
                <span>Document will be removed on save.</span>
                <button type="button" className="sup-btn" style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={() => setDocAction(null)}>Undo</button>
              </div>
            ) : (
              <>
                <input type="file" className="sup-input" style={{ padding: 6 }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp"
                  onChange={e => {
                    setFile(e.target.files?.[0] || null);
                    if (isEdit && e.target.files?.[0]) setDocAction('replace');
                  }} />
                {isEdit && docAction === 'replace' && (
                  <button type="button" className="sup-btn" style={{ padding: '2px 8px', fontSize: 11, marginTop: 4 }}
                    onClick={() => { setDocAction(currentDocName ? null : null); setFile(null); }}>Cancel</button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="sup-btn primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Contract'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function SupplierContracts() {
  const { instance: msalInstance } = useMsal();

  const tokenRef = useRef({ value: null, expiresAt: 0 });
  const acquireToken = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && tokenRef.current.value && tokenRef.current.expiresAt > now) {
      return tokenRef.current.value;
    }
    const t = await getDashboardToken(msalInstance);
    if (t) tokenRef.current = { value: t, expiresAt: now + 50 * 60 * 1000 };
    return t;
  }, [msalInstance]);

  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [detailContract, setDetailContract] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    setErr('');
    try {
      let token = await acquireToken();
      let data;
      try {
        data = await fetchContracts(token);
      } catch (e) {
        if (e?.response?.status === 401) {
          token = await acquireToken(true);
          data = await fetchContracts(token);
        } else throw e;
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setErr('Failed to load contracts.');
    }
  }, [acquireToken]);

  useEffect(() => { load(); }, [load]);

  // Load supplier directory for the form
  useEffect(() => {
    (async () => {
      try {
        const list = await listSupplierDirectory();
        setSuppliers(Array.isArray(list) ? list : []);
      } catch { /* ignore */ }
    })();
  }, []);

  const openDoc = async (contract) => {
    try {
      const token = await acquireToken();
      const resp = await fetch(`${API_URL}/api/supplier-contracts/${contract.id}/document`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch { /* ignore */ }
  };

  return (
    <div className="sup-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <span className="sup-title-main">Supplier Contracts</span>
          <span className="sup-title-sub">Manage supplier contracts and linked molecules</span>
        </div>
        <button className="sup-btn primary" onClick={() => setShowAdd(true)}>
          <i className="bi bi-plus-circle me-1"></i> Add New
        </button>
      </div>

      {err && <div style={{ color: 'var(--red, #d63b0a)', marginBottom: 10 }}>{err}</div>}

      {!rows && !err && <div style={{ color: 'var(--t3)', padding: 20 }}>Loading contracts...</div>}

      {rows && rows.length === 0 && (
        <div style={{ color: 'var(--t3)', padding: 40, textAlign: 'center' }}>
          No contracts yet. Click "Add New" to create one.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table" style={{ minWidth: 1100 }}>
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Supplier</th>
                  <th style={{ width: 200 }}>Primary Contact</th>
                  <th style={{ width: 110 }}>Signed Date</th>
                  <th style={{ width: 110 }}>Start Date</th>
                  <th style={{ width: 110 }}>End Date</th>
                  <th style={{ width: 90 }}>Document</th>
                  <th style={{ width: 70 }}>Edit</th>
                  <th style={{ width: 80 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td>{r.supplier_name || r.supplier_id}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.contact_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--t3)' }}>
                        {[r.contact_email, r.contact_phone].filter(Boolean).join(' | ')}
                      </div>
                    </td>
                    <td>{fmtDate(r.signed_date)}</td>
                    <td>{fmtDate(r.start_date)}</td>
                    <td>{fmtDate(r.end_date)}</td>
                    <td>
                      {r.document_blob_name ? (
                        <button className="sup-btn" style={{ padding: '3px 10px', fontSize: 12 }}
                          onClick={() => openDoc(r)}>
                          <i className="bi bi-file-earmark-arrow-down"></i> View
                        </button>
                      ) : null}
                    </td>
                    <td>
                      <button className="sup-btn" style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={() => setEditContract(r)}>
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                    </td>
                    <td>
                      <button className="sup-btn" style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={() => setDetailContract(r)}>
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailContract && (
        <DetailsModal
          contract={detailContract}
          token={tokenRef.current.value}
          onClose={() => setDetailContract(null)}
        />
      )}

      {showAdd && (
        <ContractFormModal
          mode="add"
          suppliers={suppliers}
          token={tokenRef.current.value}
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); load(); }}
        />
      )}

      {editContract && (
        <ContractFormModal
          mode="edit"
          contract={editContract}
          suppliers={suppliers}
          token={tokenRef.current.value}
          onClose={() => setEditContract(null)}
          onDone={() => { setEditContract(null); load(); }}
        />
      )}
    </div>
  );
}
